// ** React Imports
import { useState } from "react";

import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { Link } from "@mui/material";

// ** MUI Components
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";

import AskOTPModal from "src/layouts/components/OTP/AskOTPModal";
import {
  pathForAuthRoute,
  returnUrlHasIframeMode,
  iframeReturnUrlAsFullAppPath,
} from "src/@core/utils/urlQueryPreservation";

// ** Icons Imports
import EyeOutline from "mdi-material-ui/EyeOutline";
import EyeOffOutline from "mdi-material-ui/EyeOffOutline";
import LockOutline from "mdi-material-ui/LockOutline";
import EmailOutline from "mdi-material-ui/EmailOutline";

// ** Third Party Imports
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

// ** Hooks
import { useAuth } from "src/hooks/useAuth";

// ** Auth UI
import AuthShell from "src/views/pages/auth/AuthShell";

const schema = yup.object().shape({
  email: yup.string().email().required(),
  password: yup.string().min(4).required(),
});

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isWrongPassword, setIsWrongPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpModal, setOtpModal] = useState({ visible: false, data: null });
  const [emailAddress, setEmailAddress] = useState("");

  // ** Hooks
  const auth = useAuth();

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const parseError = (message) => {
    if (message === "email must be a valid email") {
      return "Endereço de e-mail inválido";
    }
    if (message === "email is a required field") {
      return "Endereço de e-mail é um campo obrigatório";
    }

    if (message === "password is a required field") {
      return "Senha é um campo obrigatório";
    }

    if (message === "password must be at least 4 characters") {
      return "A Senha tem de ter pelo menos 4 caracteres";
    }

    return message;
  };

  const parseLoginError = (reason) => {
    switch (reason) {
      case "record_not_found":
        setError("email", { type: "manual", message: "Não foi possível encontrar o registo." });
        break;

      case "usb_password_does_not_match":
        setError("password", { type: "manual", message: "Endereço de e-mail ou senha inválido." });
        setIsWrongPassword(true);
        setOtpModal({ visible: true, data: null });
        break;
      case "password_does_not_match":
        setError("password", { type: "manual", message: "Endereço de e-mail ou senha inválido." });
        setIsWrongPassword(true);
        setOtpModal({ visible: true, data: null });
        break;
      case "user_blocked":
        setError("email", { type: "manual", message: "Conta inativa. Entra em contato com o suporte." });
        break;
      case "user_not_active":
        setError("email", { type: "manual", message: "Utilizador inativo. Entra em contato com o suporte." });
        break;
      default:
        setError("email", { type: "manual", message: "Oops, algo correu mal..." });
        break;
    }
  };

  const onSubmit = (data) => {
    const { email, password } = data;
    setIsWrongPassword(false);
    setEmailAddress(email);
    auth.login({ email: email.toLowerCase(), password }, (a) => {
      parseLoginError(a?.response?.data?.reason);
    });
  };

  const returnUrlParam = new URLSearchParams(location.search).get("returnUrl");
  const iframeEmbedLoginBlocked = returnUrlHasIframeMode(returnUrlParam);
  const fullAppPath = iframeEmbedLoginBlocked ? iframeReturnUrlAsFullAppPath(returnUrlParam) : "/";
  const openFullAppInNewTab = () => {
    const url = `${window.location.origin}${fullAppPath}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  // ── Iframe context: login must happen in the full app ──────────────
  if (iframeEmbedLoginBlocked) {
    return (
      <AuthShell
        icon={<LockOutline />}
        eyebrow="Sessão necessária"
        title="Abre a Academy para entrar"
        subtitle="Não é possível iniciar sessão numa visualização em iframe. Por segurança, abre a versão completa da Academy numa nova aba e volta ao conteúdo."
      >
        <Button className="sp-auth__submit" variant="contained" onClick={openFullAppInNewTab}>
          Abrir Academy numa nova aba
        </Button>
      </AuthShell>
    );
  }

  // ── Standard login ─────────────────────────────────────────────────
  return (
    <>
      <AuthShell
        icon={<LockOutline />}
        eyebrow="Acesso à Academy"
        title="Bem-vindo de volta 👋"
        subtitle="Inicia sessão para continuares a tua aprendizagem."
      >
        <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
          <FormControl className="sp-auth__field" fullWidth>
            <label className="sp-auth__label" htmlFor="auth-login-email">
              Endereço de e-mail
            </label>
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <OutlinedInput
                  autoFocus
                  fullWidth
                  id="auth-login-email"
                  value={value}
                  onBlur={onBlur}
                  onChange={(e) => {
                    onChange(e.target.value.replace(/\s/g, ""));
                  }}
                  error={Boolean(errors.email)}
                  placeholder="o-teu-email@exemplo.com"
                />
              )}
            />
            {errors.email && <FormHelperText>{parseError(errors?.email?.message)}</FormHelperText>}
          </FormControl>

          <FormControl className="sp-auth__field" fullWidth>
            <label className="sp-auth__label" htmlFor="auth-login-password">
              Senha
            </label>
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <OutlinedInput
                  fullWidth
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  id="auth-login-password"
                  placeholder="A tua senha"
                  error={Boolean(errors.password)}
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOutline /> : <EyeOffOutline />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              )}
            />
            {errors.password && <FormHelperText>{parseError(errors?.password.message)}</FormHelperText>}
          </FormControl>

          <Box className="sp-auth__field-link">
            <Link component={RouterLink} to={pathForAuthRoute("/forgot-password", location.search)}>
              Esqueceste-te da senha?
            </Link>
          </Box>

          <Button className="sp-auth__submit" type="submit" variant="contained">
            Entrar
          </Button>
        </form>

        <div className="sp-auth__divider">ou</div>

        <Button
          className="sp-auth__alt"
          variant="outlined"
          component={RouterLink}
          to={pathForAuthRoute("/otp-login", location.search)}
          startIcon={<EmailOutline />}
        >
          Entrar com código por e-mail
        </Button>
      </AuthShell>

      <AskOTPModal
        visible={otpModal.visible}
        close={() => setOtpModal({ visible: false, data: null })}
        onConfirm={() => {
          // Redirect to /otp-login
          setOtpModal({ visible: false, data: null });
          navigate(pathForAuthRoute("/otp-login", location.search, { c: window.btoa(emailAddress) }));
        }}
      />
    </>
  );
};

export default LoginPage;
